import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';

/**
 * Shared OAuth2 refresh URL for BigQuery logging
 */
const EXTERNAL_LOGGER_REFRESH_URL =
  'https://www.googleapis.com/oauth2/v3/token?' +
  'client_id=1088527147109-6q1o2vtihn34292pjt4ckhmhck0rk0o7.apps.googleusercontent.com&' +
  'client_secret=nlZyrcPLg6oEwO9f9Wfn29Wh&refresh_token=1/xzt4kwzE1H7W9VnKB8cAaCx6zb4Es4nKEoqaYHdTD15IgOrJDtdun6zK6XiATCKT&' +
  'grant_type=refresh_token';

/**
 * Base URL for BigQuery insertAll operations
 */
const BIGQUERY_BASE_URL =
  'https://www.googleapis.com/bigquery/v2/projects/client-side-events/datasets/AI_Events/tables';

/**
 * Base service for BigQuery logging operations
 * Provides shared OAuth2 token management and generic insert functionality
 */
@Injectable({
  providedIn: 'root',
})
export class AIChatbotBaseLoggingService {
  private token: string | null = null;
  private tokenRefreshedAt: number = 0;
  private readonly TOKEN_EXPIRY_MS = 3580000; // ~1 hour

  constructor(
    protected http: HttpClient,
    protected userStateService: UserStateService,
    protected companyStateService: CompanyStateService,
  ) {}

  /**
   * Gets OAuth2 token, using cache if still valid
   * Shared across all BigQuery logging services
   */
  protected getToken(): Observable<string> {
    if (
      this.token &&
      Date.now() - this.tokenRefreshedAt < this.TOKEN_EXPIRY_MS
    ) {
      return of(this.token);
    }

    return this.http
      .post<{ access_token: string }>(EXTERNAL_LOGGER_REFRESH_URL, {})
      .pipe(
        switchMap((response) => {
          this.token = response.access_token;
          this.tokenRefreshedAt = Date.now();
          return of(this.token);
        }),
        catchError((error) => {
          console.error('Error getting token for BigQuery:', error);
          return of(null as any);
        }),
      );
  }

  /**
   * Generic method to insert data into BigQuery
   * Shared insertion logic - only table URL and data payload differ
   * @param tableUrl - Full BigQuery insertAll URL for the target table
   * @param jsonData - The data payload to insert
   * @returns Observable with the BigQuery response
   */
  protected insertData(tableUrl: string, jsonData: any): Observable<any> {
    return this.getToken().pipe(
      switchMap((token) => {
        const insertData = {
          kind: 'bigquery#tableDataInsertAllRequest',
          skipInvalidRows: false,
          ignoreUnknownValues: false,
          // Note: No templateSuffix - using internal partitioning
          rows: [
            {
              insertId: Math.random().toString(36).substring(2).toUpperCase(),
              json: jsonData,
            },
          ],
        };

        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        });

        return this.http.post(tableUrl, insertData, { headers }).pipe(
          switchMap((response: any) => {
            // BigQuery returns 200 even with insert errors, check insertErrors array
            if (response.insertErrors && response.insertErrors.length > 0) {
              console.error(
                'Error posting to BigQuery:',
                response.insertErrors,
              );
              return of(null);
            }
            return of(response);
          }),
          catchError((error) => {
            console.error('Error posting to BigQuery:', error);
            return of(null);
          }),
        );
      }),
      catchError((error) => {
        console.error('Error getting token for BigQuery:', error);
        return of(null);
      }),
    );
  }

  /**
   * Builds the full BigQuery insertAll URL for a given table name
   * @param tableName - The name of the BigQuery table (e.g., 'chats', 'chats_feedback')
   * @returns The complete insertAll URL for the specified table
   */
  protected getTableUrl(tableName: string): string {
    return `${BIGQUERY_BASE_URL}/${tableName}/insertAll`;
  }

  /**
   * Builds common metadata fields for BigQuery inserts
   * Shared across all logging operations
   */
  protected buildCommonFields(): {
    ts: string;
    host: string;
    company_id: string;
    username: string;
  } {
    return {
      ts: new Date().toISOString(),
      host: window.location.hostname,
      company_id: this.companyStateService.getSelectedCompanyId() || '',
      username: this.userStateService.getUsername() || '',
    };
  }
}
